import React, { useEffect, useState } from 'react';
import Container from 'react-bootstrap/Container';
import { getGiftCardRedeemInfo, postRedeemGiftCard, postVerifyGiftCardCode } from '../../../backend-api';
import { Button } from '../../../components';
import { t } from '../../../i18n';
import UrlTo from '../../../url-to';
import useThrottledCallback from '../../../utilities/hooks/useThrottledCallback';
import { logError } from '../../../utilities/log';
import BreadCrumbs from '../../components/breadcrumbs';
import LineLoader from '../../components/line-loader';
import useCurrentUser from '../../current-user/useCurrentUser';
import RegisterMiniForm from '../checkout/components/register-mini-form';
import ActivationCodeInput from './components/activation-code-input';
import RedeemCoursesList from './components/redeem-courses-list';
import CourseItem from './components/course-item';
import RedeemStatus from './components/redeem-status';
import './styles.scss';

const breadCrumbItems = [
  {
    url: UrlTo.coursesHome(),
    name: t('DIGITAL_COURSES.HOME_SCREEN.NAME'),
  },
  {
    isActive: true,
    name: t('DIGITAL_COURSES.SPECIFIC_COURSE_SCREEN.ACTIVATE_GIFT_CARD'),
  },
];

const GiftCardRedeem = ({ history }) => {
  const [availableCourses, setAvailableCourses] = useState(null);
  const [areCoursesLoading, setCoursesLoading] = useState(false);
  const [isCoursesError, setCoursesError] = useState(false);
  const [activationCode, setActivationCode] = useState('');
  const [anonymousUserInfo, setAnonymousUserInfo] = useState(null);
  const [selectedCourseId, setSelectedCourseId] = useState(() => {
    const query = new URLSearchParams(window.location.search);
    return Number(query.get('courseId') || 1);
  });
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [redeemStatus, setRedeemStatus] = useState(null);
  const { currentUser } = useCurrentUser();

  const checkGiftCardStatusThrottled = useThrottledCallback(
    async redeemCode => {
      try {
        const { error } = await postVerifyGiftCardCode(redeemCode);
        setRedeemStatus(error ? { error: `DIGITAL_COURSES.CHECKOUT.ERROR.${error}` } : { success: true });
      } catch (e) {
        setRedeemStatus({ error: 'SERVER_ERROR' });
      }
    },
    [],
    { wait: 500 },
  );

  const handleActivationCodeChange = code => {
    if (code === activationCode) {
      return;
    }
    setActivationCode(code);
    if (code) {
      setRedeemStatus({ loading: true });
      checkGiftCardStatusThrottled(code);
    }
  };

  const loadCourses = async () => {
    setCoursesLoading(true);
    setCoursesError(false);
    try {
      const { courses } = await getGiftCardRedeemInfo();
      setAvailableCourses(courses);
    } catch (e) {
      logError('GiftCardRedeem', "Couldn't load courses to redeem", { message: e.message });
      setCoursesError(true);
    }
    setCoursesLoading(false);
  };

  useEffect(() => {
    loadCourses();
  }, []);

  const redeemGiftCard = async ({ email, name }) => {
    setRedeemStatus({ loading: true });
    try {
      await postRedeemGiftCard({ email, name, redeemCode: activationCode, courseId: selectedCourseId });
      history.push(UrlTo.coursesGiftCardActivated());
    } catch (e) {
      const error = e.response?.data.error;
      setRedeemStatus({ error: error ? `DIGITAL_COURSES.CHECKOUT.ERROR.${error}` : 'SERVER_ERROR' });
    }
  };

  const handleSubmitCode = () => {
    if (currentUser) {
      redeemGiftCard(currentUser);
      return;
    }
    setShowRegistrationForm(true);
  };

  const handleEnterRegistrationInfo = ({ email, name }) => {
    setAnonymousUserInfo({ email, name });
    redeemGiftCard({ email, name });
  };

  const selectedCourse = availableCourses?.find(c => c.id === selectedCourseId);

  return (
    <div className="course-gift-card-redeem">
      <LineLoader hide={!areCoursesLoading} />
      <Container>
        <BreadCrumbs navigationLinks={breadCrumbItems} />
        <h1>{t('DIGITAL_COURSES.SPECIFIC_COURSE_SCREEN.ACTIVATE_GIFT_CARD')}</h1>
        <div className="course-gift-card-redeem__activation-form">
          <div className="course-gift-card-redeem__course-preview">
            {!!selectedCourse ? <CourseItem course={selectedCourse} /> : <CourseItem.Placeholder />}
          </div>
          {!showRegistrationForm && (
            <div className="course-gift-card-redeem__activation-code-column">
              <div className="course-gift-card-redeem__activation-code">
                <ActivationCodeInput onCodeEntered={handleActivationCodeChange} initialCode={activationCode} />
                <div className="course-gift-card-redeem__submit-button-row">
                  <RedeemStatus redeemStatus={redeemStatus} className="course-gift-card-redeem__verify-status" />
                  <Button
                    className="course-gift-card-redeem__submit-button"
                    type="green"
                    text={t('DIGITAL_COURSES.GIFT_CARD_REDEEM.REDEEM_COURSE')}
                    disabled={!selectedCourse}
                    onClick={handleSubmitCode}
                  />
                </div>
              </div>
              {selectedCourse && (
                <p
                  className="course-gift-card-redeem__instructions"
                  dangerouslySetInnerHTML={{
                    __html: t('DIGITAL_COURSES.GIFT_CARD_REDEEM.INSTRUCTIONS', { courseTitle: selectedCourse.title }),
                  }}
                />
              )}
              {!selectedCourse && (
                <div className="placeholder-glow course-gift-card-redeem__instructions--placeholder">
                  <p className="placeholder" />
                </div>
              )}
            </div>
          )}
          {showRegistrationForm && (
            <div>
              <RegisterMiniForm
                email={anonymousUserInfo?.email}
                name={anonymousUserInfo?.name}
                onEnterData={handleEnterRegistrationInfo}
                className="course-gift-card-redeem__register"
              />
              <Button
                type="white"
                onClick={() => setShowRegistrationForm(false)}
                text={t('BACK')}
                className="course-gift-card-redeem__back-button"
              />
              <RedeemStatus redeemStatus={redeemStatus} className="course-gift-card-redeem__registration-status" />
            </div>
          )}
        </div>
        <RedeemCoursesList
          courses={availableCourses}
          isLoading={areCoursesLoading}
          isError={isCoursesError}
          selectedCourseId={selectedCourseId}
          onSelectedCourseIdChange={setSelectedCourseId}
        />
      </Container>
    </div>
  );
};

export default GiftCardRedeem;
